import { PromoChannel, PromoCodeDetail, PromoCustomer, ShareOutcome, ShareRecipient, ShareResult } from '../../types/promotions';
import { discountText, formatExpiry } from './promoFormat';

export type ShareTab = 'CUSTOMERS' | 'EMAIL' | 'PHONE';
export type ChannelChoice = 'WHATSAPP' | 'EMAIL' | 'BOTH';

/** G-21 — at most 50 recipients per share action. */
export const SHARE_BATCH_MAX = 50;
export const INVALID_TARGET_MESSAGE = 'Enter a valid email / WhatsApp number.';
export const WHATSAPP_UNAVAILABLE = "WhatsApp sharing isn't enabled yet";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isValidEmail(raw: string): boolean {
  return EMAIL_RE.test(raw.trim());
}

/** Loose client check; the server normalises to E.164 with the UAE default prefix (BR-25). */
export function isValidPhone(raw: string): boolean {
  const cleaned = raw.trim().replace(/[\s\-()]/g, '').replace(/^(\+|00)/, '');
  return /^\d{7,15}$/.test(cleaned);
}

/** AC-14 — a customer without an email always gets WhatsApp, whatever the batch channel says. */
export function customerChannels(c: PromoCustomer, choice: ChannelChoice): PromoChannel[] {
  if (!c.hasEmail) return ['WHATSAPP'];
  if (choice === 'BOTH') return ['WHATSAPP', 'EMAIL'];
  return [choice];
}

export function buildRecipients(tab: ShareTab, customers: PromoCustomer[], choice: ChannelChoice, email: string, phone: string): ShareRecipient[] {
  if (tab === 'EMAIL') return [{ type: 'EMAIL', email: email.trim().toLowerCase() }];
  if (tab === 'PHONE') return [{ type: 'PHONE', phone: phone.trim() }];
  return customers.map((c) => ({ type: 'CUSTOMER', subjectId: c.subjectId, channels: customerChannels(c, choice) }));
}

export const OUTCOME_LABEL: Record<ShareOutcome, string> = {
  SHARED: 'Shared',
  ALREADY_HOLDS: 'Already holds it — message re-sent',
  FULL: 'No slots left',
  INVALID_TARGET: 'Invalid email / WhatsApp number',
  CHANNEL_UNAVAILABLE: WHATSAPP_UNAVAILABLE,
};

export const OUTCOME_CLASS: Record<ShareOutcome, string> = {
  SHARED: 'approved', ALREADY_HOLDS: 'update-requested', FULL: 'warning', INVALID_TARGET: 'rejected', CHANNEL_UNAVAILABLE: 'neutral',
};

/** Who a result row is about — from the request (same order), falling back to the echoed recipient. */
export function recipientLabel(sent: ShareRecipient | undefined, result: ShareResult, customers: PromoCustomer[]): string {
  const r = sent ?? (typeof result.recipient === 'object' ? result.recipient : null);
  if (!r) return typeof result.recipient === 'string' ? result.recipient : 'Recipient';
  if (r.type === 'EMAIL') return r.email;
  if (r.type === 'PHONE') return r.phone;
  const c = customers.find((x) => x.subjectId === r.subjectId);
  return c?.name || c?.phone || c?.email || r.subjectId;
}

/** Toast after a share: "Shared with 2 · 1 already held · 1 not shared". */
export function summarize(results: ShareResult[]): string {
  const shared = results.filter((r) => r.outcome === 'SHARED').length;
  const held = results.filter((r) => r.outcome === 'ALREADY_HOLDS').length;
  const failed = results.length - shared - held;
  return [`Shared with ${shared}`, held ? `${held} already held` : '', failed ? `${failed} not shared` : ''].filter(Boolean).join(' · ');
}

/** What the recipient reads (template params: name, discount, code, expiry, link) + opt-out (D-18). */
export function previewMessage(promo: PromoCodeDetail): string {
  const uses = promo.usesPerHolder === 1 ? 'Use it once' : `Use it up to ${promo.usesPerHolder} times`;
  const expiry = promo.expiresAt ? ` before ${formatExpiry(promo.expiresAt)}` : '';
  return `Hi {name}, enjoy ${discountText(promo)} your next Garagat car service with code ${promo.code}. `
    + `${uses}${expiry}. Add it to your wallet: ${promo.shareLink} — Reply STOP to opt out.`;
}
